import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { FONT_BODY, FONT_DISPLAY, FONT_MONO, EASE } from './theme'

// Shared 2D design system for the CV explainer scenes.
export const COLORS = {
  indigo: '#6366f1',
  indigoLight: '#818cf8',
  purple: '#a855f7',
  pink: '#ec4899',
  green: '#22c55e',
  red: '#ef4444',
  blue: '#3b82f6',
  amber: '#fbbf24',
  text: 'rgba(255,255,255,0.92)',
  textDim: 'rgba(255,255,255,0.55)',
  textFaint: 'rgba(255,255,255,0.35)',
}

export { FONT_BODY, FONT_DISPLAY, FONT_MONO, EASE }

export const sceneShell = {
  position: 'absolute', inset: 0,
  display: 'flex', flexDirection: 'column',
  overflowY: 'auto',
  alignItems: 'center', justifyContent: 'center',
  gap: 6, padding: '90px 24px 120px',
  color: COLORS.text,
  fontFamily: FONT_BODY,
  textAlign: 'center',
}

// Glass panel with a gradient hairline border + soft inner highlight.
export function Panel({ children, style, glow = COLORS.indigo }) {
  return (
    <div style={{
      position: 'relative',
      background: 'linear-gradient(180deg, rgba(18,18,34,0.6), rgba(8,8,18,0.55))',
      border: '1px solid transparent',
      borderRadius: 16,
      padding: 16,
      backdropFilter: 'blur(14px)',
      boxShadow: `0 10px 40px rgba(0,0,0,0.35), 0 0 50px ${glow}12, inset 0 1px 0 rgba(255,255,255,0.06)`,
      backgroundClip: 'padding-box',
      overflow: 'hidden',
      ...style,
    }}>
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 'inherit', padding: 1, pointerEvents: 'none',
        background: `linear-gradient(160deg, ${glow}66, rgba(255,255,255,0.04) 40%, transparent 70%)`,
        WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        WebkitMaskComposite: 'xor', maskComposite: 'exclude',
      }} />
      <div style={{
        position: 'absolute', top: 0, left: '8%', right: '8%', height: 1,
        background: `linear-gradient(90deg, transparent, ${glow}cc, ${glow}, ${glow}cc, transparent)`,
        boxShadow: `0 0 18px ${glow}70`, pointerEvents: 'none',
      }} />
      {children}
    </div>
  )
}

// Pill badge with a glowing dot, matching the AI/Audio explainer chrome.
export function Badge({ children, color = COLORS.indigoLight }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '5px 14px 5px 10px', borderRadius: 100,
      background: `${color}14`, border: `1px solid ${color}38`,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}` }} />
      <span style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontFamily: FONT_MONO, color, fontWeight: 700 }}>
        {children}
      </span>
    </span>
  )
}

export function Caption({ children }) {
  return (
    <motion.p
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
      style={{ maxWidth: 680, margin: '24px auto 0', fontSize: 14.5, lineHeight: 1.65, color: COLORS.textDim, fontFamily: FONT_BODY }}
    >
      {children}
    </motion.p>
  )
}

export function Tag({ children, color = COLORS.indigoLight }) {
  return (
    <motion.span
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: EASE }}
      style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontFamily: FONT_MONO, color, display: 'inline-block' }}
    >
      {children}
    </motion.span>
  )
}

// Big display heading with a line-mask reveal (text clips up into view).
export function SceneTitle({ children, color = COLORS.text, size = 'clamp(26px, 4vw, 44px)' }) {
  return (
    <div style={{ overflow: 'hidden', padding: '0.06em 0' }}>
      <motion.h2
        initial={{ y: '110%' }}
        animate={{ y: '0%' }}
        transition={{ duration: 0.8, ease: EASE }}
        style={{ margin: 0, fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: size, letterSpacing: '-0.02em', lineHeight: 1.05, color }}
      >
        {children}
      </motion.h2>
    </div>
  )
}

// Animated count-up number.
export function Ticker({ value, decimals = 0, suffix = '', duration = 1100 }) {
  const [display, setDisplay] = useState(0)
  const ref = useRef({ from: 0 })
  useEffect(() => {
    const from = ref.current.from
    const start = performance.now()
    let raf
    const tick = now => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3)
      setDisplay(from + (value - from) * eased)
      if (t < 1) raf = requestAnimationFrame(tick)
      else ref.current.from = value
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value, duration])
  return <>{display.toFixed(decimals)}{suffix}</>
}
